/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import type { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';

type AxiosRetryState = {
    retryCount: number;
};

type AxiosRetryConfig = {
    maxRetries: number;
    isRetryableError: (error: AxiosError) => boolean;
    retryDelay?: (retryCount: number) => number;
    shouldRetry?: (config: AxiosRetryConfig, error: AxiosError) => boolean;
    __state: AxiosRetryState;
};

type CustomAxiosRetryConfig = Partial<AxiosRetryConfig>;

const isRetryableError = (error: AxiosError) => {
    return (
        error.code !== 'ECONNABORTED' &&
        (!error.response ||
            error.response.status === 429 ||
            (error.response.status >= 500 && error.response.status <= 599))
    );
};

const shouldRetry = (cfg: AxiosRetryConfig, error: AxiosError) => {
    if (!isRetryableError(error)) {
        return false;
    }

    if (cfg.__state.retryCount > cfg.maxRetries) {
        return false;
    }

    return true;
};

// used to respect the retry header on the response
const retryAfter = (error: AxiosError | undefined = undefined): number => {
    const retryAfterHeader = error?.response?.headers['retry-after'];
    if (!retryAfterHeader) {
        return 0;
    }
    let retryAfterMs = (Number(retryAfterHeader) || 0) * 1000;
    if (retryAfterMs === 0) {
        retryAfterMs = (new Date(retryAfterHeader).valueOf() || 0) - Date.now();
    }
    return Math.max(0, retryAfterMs);
};

const retryDelay = (cfg: AxiosRetryConfig, error?: AxiosError) => {
    const { retryCount } = cfg.__state;
    const delay = 2 ** retryCount * 1000; // Exponential backoff
    return Math.max(retryAfter(error), Math.min(delay, 30000)); // Cap delay at 30 seconds
};

const handleRetry = (
    axiosInstance: AxiosInstance,
    cfg: AxiosRetryConfig,
    axiosCfg: AxiosRequestConfig,
    error: AxiosError
) => {
    cfg.__state.retryCount += 1;
    return new Promise((resolve) => {
        setTimeout(
            () => {
                resolve(axiosInstance(axiosCfg));
            },
            retryDelay(cfg, error)
        );
    });
};

const defaultRetryConfig: AxiosRetryConfig = {
    maxRetries: 3,
    isRetryableError,
    __state: {
        retryCount: 0,
    },
};

export default function AxiosRetry(
    axiosInstance: AxiosInstance,
    retryConfigOverrides?: CustomAxiosRetryConfig
) {
    const retryConfig: AxiosRetryConfig = {
        ...defaultRetryConfig,
        ...retryConfigOverrides,
    };

    const interceptorId = axiosInstance.interceptors.response.use(null, async (error) => {
        const { config } = error;
        if (!config) {
            return Promise.reject(error);
        }

        if (!shouldRetry(retryConfig, error)) {
            return Promise.reject(error);
        }

        handleRetry(axiosInstance, retryConfig, config, error);

        return axiosInstance(config);
    });

    return interceptorId;
}
